import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import {
    ChecklistOutlined,
    Diversity3Outlined,
    EmojiEventsOutlined,
    ForumOutlined,
    GavelOutlined,
    ScheduleOutlined,
    ShieldOutlined,
} from "@mui/icons-material";
import { colors } from "../../theme/colors";

export const REPUTATION_CARD_SX = {
    display: "flex",
    flexDirection: "column",
    borderRadius: "12px",
    position: "relative",
    overflow: "hidden",
    background: `
        linear-gradient(
            180deg,
            rgba(12,12,14,0.96) 0%,
            rgba(8,8,10,0.985) 100%
        )
    `,
    border: "1px solid rgba(255,255,255,0.05)",
    backdropFilter: "blur(18px)",
    boxShadow: `
        inset 0 1px 0 rgba(255,255,255,0.05),
        inset 0 -40px 80px rgba(246,185,76,0.03),
        0 24px 60px rgba(0,0,0,0.42)
    `,
    gap: "0.75rem",
    padding: "1.5rem",
    minWidth: 0,
};

export const CARD_TOP_GLOW_SX = {
    position: "absolute",
    top: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: "42%",
    height: "1px",
    background: `
        linear-gradient(
            90deg,
            transparent,
            rgba(246,185,76,0.95),
            transparent
        )
    `,
    boxShadow: "0 0 18px rgba(246,185,76,0.45)",
    pointerEvents: "none",
};

export const REPUTATION_DIMENSIONS = [
    { key: "sportsmanship", label: "Sportsmanship", description: "Fair play, no toxicity", icon: EmojiEventsOutlined },
    { key: "integrity", label: "Integrity", description: "No cheating or smurfing", icon: ShieldOutlined },
    { key: "communication", label: "Communication", description: "Clear callouts, good comms", icon: ForumOutlined },
    { key: "teamPlay", label: "Team play", description: "Plays for the team", icon: Diversity3Outlined },
    { key: "reliability", label: "Reliability", description: "Shows up, doesn't AFK", icon: ScheduleOutlined },
    { key: "reporterQuality", label: "Reporter quality", description: "Reports filed are accurate", icon: GavelOutlined },
    { key: "profileCompleteness", label: "Profile completeness", description: "Account details and verification", icon: ChecklistOutlined },
];

export default function ReputationDimensionCard({
    label,
    score = 0,
    description,
    icon: Icon,
}) {
    const safeScore = Number.isFinite(score) ? Math.max(0, Math.min(100, score)) : 0;

    return (
        <Box
            sx={{
                ...REPUTATION_CARD_SX,
                width: "100%",
                boxSizing: "border-box",
                padding: "1.15rem 1.25rem",
            }}
        >
            <Box sx={{ position: "relative", zIndex: 2, display: "flex", flexDirection: "column", gap: "0.85rem" }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.75rem" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "0.65rem", minWidth: 0 }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "2.1rem",
                                height: "2.1rem",
                                borderRadius: "10px",
                                flexShrink: 0,
                                backgroundColor: "rgba(232,163,61,0.1)",
                                border: "1px solid rgba(232,163,61,0.18)",
                                color: colors.primary.main,
                            }}
                        >
                            <Icon sx={{ fontSize: "1.1rem" }} />
                        </Box>
                        <Typography
                            sx={{
                                fontSize: "0.78rem",
                                fontWeight: 700,
                                letterSpacing: "0.03em",
                                color: colors.text.primary,
                                lineHeight: 1.25,
                            }}
                        >
                            {label}
                        </Typography>
                    </Box>
                    <Typography
                        sx={{
                            fontSize: "1rem",
                            fontWeight: 800,
                            color: colors.primary.main,
                            lineHeight: 1,
                            fontVariantNumeric: "tabular-nums",
                            flexShrink: 0,
                        }}
                    >
                        {safeScore}
                        <Box
                            component="span"
                            sx={{
                                ml: "0.15rem",
                                fontSize: "0.72rem",
                                fontWeight: 600,
                                color: "rgba(255,255,255,0.38)",
                            }}
                        >
                            /100
                        </Box>
                    </Typography>
                </Box>

                <Box
                    sx={{
                        width: "100%",
                        height: "0.4rem",
                        borderRadius: "999px",
                        backgroundColor: "rgba(255,255,255,0.08)",
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            width: `${safeScore}%`,
                            height: "100%",
                            borderRadius: "999px",
                            background: `linear-gradient(90deg, ${colors.primary.main}, ${colors.accent.main})`,
                        }}
                    />
                </Box>

                <Typography
                    sx={{
                        fontSize: "0.68rem",
                        fontWeight: 500,
                        color: "rgba(255,255,255,0.38)",
                        lineHeight: 1.3,
                    }}
                >
                    {description}
                </Typography>
            </Box>
        </Box>
    );
}

ReputationDimensionCard.propTypes = {
    label: PropTypes.string.isRequired,
    score: PropTypes.number,
    description: PropTypes.string.isRequired,
    icon: PropTypes.elementType.isRequired,
};
